import { sumUsage, TieredPriceProductId } from "@project/common";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { buildBillSummary } from "../utils/billingAggregators.js";
import type { UsageCardLoaderProps } from "../UsageCard.js";
import type { BillSummary, ProductUsageSums } from "../utils/billingAggregators.js";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../../../../trpc.js";
import { CACHING_OPTIONS } from "../constants/cachingOptions.js";
import type { BasicUsageParameters, To, SpecificUsageParameters } from "../UsageChartWrapper.js";
import {
  useAssistantUsage,
  useProcessingUsage,
  useStyleJsonUsage,
  useTilesRequestsUsage,
} from "./fetchUsages.js";

export function useAITokensUsageSums(p: BasicUsageParameters) {
  const {
    assistantUsage,
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  } = useAssistantUsage(p);

  return {
    inputTokensUsageSum: sumUsage(assistantUsage?.input ?? []),
    outputTokensUsageSum: sumUsage(assistantUsage?.output ?? []),
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  };
};

export function useStyleJsonUsageSum(p: SpecificUsageParameters) {
  const {
    styleJsonUsage,
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  } = useStyleJsonUsage(p);

  return {
    usageSum: sumUsage(styleJsonUsage),
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  };
};

export function useProcessingUsageSum(p: SpecificUsageParameters) {
  const {
    processingUsage,
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  } = useProcessingUsage(p);

  return {
    usageSum: sumUsage(processingUsage),
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  };
};

export function useTilesRequestsUsageSum(p: SpecificUsageParameters) {
  const {
    tilesRequestsUsage,
    isLoadingTilesUsage,
    isErrorTilesUsage,
  } = useTilesRequestsUsage(p);

  return {
    usageSum: sumUsage(tilesRequestsUsage),
    isLoadingTilesUsage,
    isErrorTilesUsage,
  };
};

export function useStorageUsageSum(p: To) {
  const trpc = useTRPC();
  const {
    data: usageSum = 0,
    isLoading: isLoadingStorage,
    isError: isErrorStorage,
  } = useQuery(trpc.getStorageUsage.queryOptions(p));

  return { usageSum, isLoadingStorage, isErrorStorage };
};

export function useCustomStylesUsageSum(p: To) {
  const trpc = useTRPC();
  const {
    data: usageSum = 0,
    isLoading: isLoadingStyles,
    isError: isErrorStyles,
  } = useQuery(trpc.getCustomStyleUsageSum.queryOptions(p, CACHING_OPTIONS));

  return {
    usageSum,
    isLoadingStyles,
    isErrorStyles,
  };
};

export function useCustomTilesetUsageSum(p: To) {
  const trpc = useTRPC();
  const {
    data: usageSum = 0,
    isLoading: isLoadingTilesets,
    isError: isErrorTilesets,
  } = useQuery(trpc.getCustomTilesetUsageSum.queryOptions(p, CACHING_OPTIONS));

  return {
    usageSum,
    isLoadingTilesets,
    isErrorTilesets,
  };
};

export function useTeamSeatsUsageSum(p: To) {
  const trpc = useTRPC();
  const {
    data: usageSum = 0,
    isLoading: isLoadingTeamSeats,
    isError: isErrorTeamSeats,
  } = useQuery(trpc.getTeamSeatsUsageSum.queryOptions(p, CACHING_OPTIONS));

  return {
    usageSum,
    isLoadingTeamSeats,
    isErrorTeamSeats,
  };
};

export function useAllUsageSums({
  from,
  to,
  interval,
  apiKeyId,
}: SpecificUsageParameters) {
  const {
    inputTokensUsageSum,
    outputTokensUsageSum,
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  } = useAITokensUsageSums({ from, to, interval });

  const {
    usageSum: mapLoadsUsageSum,
    isLoadingStyleJsonUsage,
    isErrorStyleJsonUsage,
  } = useStyleJsonUsageSum({ from, to, interval, apiKeyId });

  const {
    usageSum: processingUsageSum,
    isLoadingProcessingUsage,
    isErrorProcessingUsage,
  } = useProcessingUsageSum({ from, to, interval, apiKeyId });

  const {
    usageSum: tilesRequestsUsageSum,
    isLoadingTilesUsage,
    isErrorTilesUsage,
  } = useTilesRequestsUsageSum({ from, to, interval, apiKeyId });

  const {
    usageSum: customStylesUsageSum,
    isLoadingStyles,
    isErrorStyles,
  } = useCustomStylesUsageSum({ to });

  const {
    usageSum: customTilesetUsageSum,
    isLoadingTilesets,
    isErrorTilesets,
  } = useCustomTilesetUsageSum({ to });

  const {
    usageSum: membersUsageSum,
    isLoadingTeamSeats,
    isErrorTeamSeats,
  } = useTeamSeatsUsageSum({ to });

  const {
    usageSum: storageUsageSum,
    isLoadingStorage,
    isErrorStorage,
  } = useStorageUsageSum({ to });

  const loadingStates = [
    isLoadingAssistantUsage,
    isLoadingStyles,
    isLoadingTilesets,
    isLoadingStyleJsonUsage,
    isLoadingTeamSeats,
    isLoadingProcessingUsage,
    isLoadingStorage,
    isLoadingTilesUsage,
  ];

  const errorStates = [
    isErrorAssistantUsage,
    isErrorStyleJsonUsage,
    isErrorProcessingUsage,
    isErrorTilesUsage,
    isErrorStyles,
    isErrorTilesets,
    isErrorTeamSeats,
    isErrorStorage,
  ];

  const isLoading = loadingStates.some(Boolean);
  const isError = errorStates.some(Boolean);

  const usageSums: ProductUsageSums = useMemo(() => ({
    [TieredPriceProductId.TieredInputToken]: inputTokensUsageSum,
    [TieredPriceProductId.TieredOutputToken]: outputTokensUsageSum,
    [TieredPriceProductId.TieredCustomStyle]: customStylesUsageSum,
    [TieredPriceProductId.TieredCustomTileset]: customTilesetUsageSum,
    [TieredPriceProductId.TieredMapSession]: mapLoadsUsageSum,
    [TieredPriceProductId.TieredAccountUser]: membersUsageSum,
    [TieredPriceProductId.TieredProcessing]: processingUsageSum,
    [TieredPriceProductId.TieredStorage]: storageUsageSum,
    [TieredPriceProductId.TieredTileUsage]: tilesRequestsUsageSum,
  }), [
    inputTokensUsageSum,
    outputTokensUsageSum,
    customStylesUsageSum,
    customTilesetUsageSum,
    mapLoadsUsageSum,
    membersUsageSum,
    processingUsageSum,
    storageUsageSum,
    tilesRequestsUsageSum,
  ]);

  return { usageSums, isLoading, isError };
};

interface UseEstimatedBillProps extends Omit<UsageCardLoaderProps, "productId"> {
  productIds: TieredPriceProductId[];
}

interface UseEstimatedBillResult {
  bill: BillSummary | null;
  isLoading: boolean;
  isError: boolean;
}

export function useEstimatedBill({
  from,
  to,
  interval,
  apiKeyId,
  productIds,
  isLoadingSubscriptions,
}: UseEstimatedBillProps): UseEstimatedBillResult {
  const intl = useIntl();
  const {
    usageSums,
    isLoading: isLoadingUsageSums,
    isError,
  } = useAllUsageSums({ from, to, interval, apiKeyId });
  const isLoading = isLoadingUsageSums || isLoadingSubscriptions;

  const bill = useMemo<BillSummary | null>(() =>
    !(isLoading || isError) ?
        buildBillSummary(productIds, usageSums, intl) :
      null,
  [
    isLoading,
    isError,
    usageSums,
    intl,
    productIds,
  ]);

  return { bill, isLoading, isError };
}
